/**
 * Gift Box Handler Script
 * Handles PDP & Cart interaction for Gift Box add-on (Free over ₹20k / Paid under ₹20k)
 *
 * FIXES in this version:
 * 1. PDP form binding now covers ALL matching forms on the page (quick-add /
 *    quick-view forms included), not just the first one.
 * 2. Cart-side toggle no longer shares a single global "isUpdating" lock with
 *    the threshold auto-check — each has its own guard so one doesn't
 *    silently block the other.
 * 3. All cart/add.js and cart/change.js calls now check response.ok and
 *    throw on non-2xx (e.g. sold-out variant), so the checkbox reverts and
 *    the user sees a console error instead of a checkbox that's checked but
 *    did nothing.
 * 4. Optional free<->paid variant auto-swap: if the widget provides BOTH
 *    data-gift-variant-id (paid) and data-gift-free-variant-id (free), the
 *    script swaps automatically when the cart crosses ₹20k in either
 *    direction. If only the paid variant id is present, behavior falls back
 *    to the original: auto-remove the gift box if the cart drops below the
 *    threshold, no auto-add above it (manual toggle only).
 * 5. Cart UI re-init after drawer refresh no longer relies on a single fixed
 *    setTimeout — it retries via requestAnimationFrame + a fallback timeout,
 *    and binding is idempotent so double-calls are harmless.
 */

(function () {
  const THRESHOLD = 2000000; // ₹20,000 in paise (smallest currency unit)

  // Separate locks: one for user-driven cart toggles, one for the automatic
  // threshold check. They must NOT share a flag — otherwise one silently
  // blocks the other and the UI goes out of sync with the actual cart.
  let isToggling = false;
  let isThresholdChecking = false;

  document.addEventListener('DOMContentLoaded', () => {
    initGiftBox();
    installCartFetchInterceptor();
  });

  document.addEventListener('shopify:section:load', () => {
    initGiftBox();
  });

  // Listen to Symmetry theme cart after-merge event
  document.addEventListener('on:cart:after-merge', () => {
    checkCartThresholdAndUpdate();
    refreshGiftBoxWidgetsUI();
  });

  function initGiftBox() {
    bindPDPFormInputs();
    bindCartGiftBoxToggles();
    refreshGiftBoxWidgetsUI();
  }

  /**
   * Both gift-box-cart-widget.liquid and gift-box-product-option.liquid are
   * rendered ENTIRELY server-side from the `cart` object — the banner text,
   * the FREE/paid badge, and the checkbox's checked state are all baked in
   * at render time. The Symmetry cart drawer's quantity +/- buttons update
   * the cart but do NOT re-render these snippets, so they go stale until a
   * full page reload.
   *
   * Fix: `installCartFetchInterceptor()` wraps window.fetch once and, after
   * ANY successful cart-mutating call (add/change/update/clear — whoever
   * made it, including the theme's own quantity buttons), calls
   * refreshGiftBoxWidgetsUI() to recompute and patch the DOM directly, so
   * both widgets stay live without depending on a section re-render.
   */
  function installCartFetchInterceptor() {
    if (window.__giftBoxFetchPatched) return;
    window.__giftBoxFetchPatched = true;

    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      try {
        const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        const isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(requestUrl);
        if (isCartMutation && response.ok) {
          // Fire and forget — don't delay whoever made the original call.
          refreshGiftBoxWidgetsUI();
        }
      } catch (_) {
        // never let widget-sync errors break the caller's fetch
      }
      return response;
    };
  }

  /** ₹X,XXX.00 INR — matches this store's money format (see cart screenshots). */
  function formatMoneyINR(cents) {
    const amount = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${amount} INR`;
  }

  /**
   * Fetches the current cart once and patches every gift-box widget on the
   * page to match it: cart-drawer banner + badge + checkbox state, and the
   * PDP option's FREE/paid badge + subtitle.
   */
  async function refreshGiftBoxWidgetsUI() {
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await res.json();
      updateCartWidgetDom(cart);
      updatePdpWidgetDom(cart);
      syncCartCheckboxesFromCart(cart);
    } catch (err) {
      console.error('Error refreshing gift box widgets:', err);
    }
  }

  function syncCartCheckboxesFromCart(cart) {
    document.querySelectorAll('.js-toggle-cart-gift-box').forEach((checkbox) => {
      const variantId = checkbox.dataset.variantId;
      if (!variantId) return;

      const matchingItem = cart.items.find(item => String(item.variant_id) === String(variantId));
      if (matchingItem) {
        checkbox.checked = true;
        checkbox.dataset.lineKey = matchingItem.key;
      } else {
        checkbox.checked = false;
        delete checkbox.dataset.lineKey;
      }
    });
  }

  function updateCartWidgetDom(cart) {
    const widget = document.querySelector('.gift-box-cart-widget');
    if (!widget) return;

    const threshold = parseInt(widget.dataset.threshold, 10) || THRESHOLD;
    const variantId = widget.dataset.giftVariantId;
    const priceFormatted = widget.dataset.giftPriceFormatted; // set by the Liquid snippet
    const isQualified = cart.total_price >= threshold;
    const giftItem = cart.items.find(item => String(item.variant_id) === String(variantId));

    widget.dataset.cartTotal = cart.total_price;
    widget.dataset.giftInCart = giftItem ? 'true' : 'false';
    widget.dataset.giftLineKey = giftItem ? giftItem.key : '';

    const msgEl = widget.querySelector('.gift-box-cart-widget__msg');
    if (msgEl) {
      if (isQualified) {
        msgEl.innerHTML = '<span class="text-success">🎉 Cart value over ₹20,000! Your Gift Box is <strong>FREE</strong>.</span>';
      } else {
        const needed = Math.max(threshold - cart.total_price, 0);
        msgEl.innerHTML = `Add <strong>${formatMoneyINR(needed)}</strong> more to unlock a <strong>FREE Gift Box</strong>!`;
      }
    }

    const btnTextEl = widget.querySelector('.gift-box-cart-widget__btn-text');
    if (btnTextEl) {
      const priceText = priceFormatted || (giftItem ? formatMoneyINR(giftItem.price) : '');
      if (giftItem) {
        btnTextEl.textContent = isQualified ? 'Gift Box Added (FREE)' : `Gift Box Added (${priceText})`;
      } else {
        btnTextEl.textContent = isQualified ? 'Add FREE Gift Box' : `Add Gift Box (+${priceText})`;
      }
    }
  }

  function updatePdpWidgetDom(cart) {
    const widget = document.querySelector('.gift-box-product-option');
    if (!widget) return;

    const threshold = parseInt(widget.dataset.threshold, 10) || THRESHOLD;
    const isFree = cart.total_price >= threshold;

    const badgeEl = widget.querySelector('.gift-box-product-option__badge');
    if (badgeEl) {
      badgeEl.classList.toggle('gift-box-product-option__badge--free', isFree);
      if (isFree) {
        badgeEl.textContent = 'FREE (Complementary)';
      } else {
        const priceFormatted = widget.dataset.giftPriceFormatted;
        badgeEl.textContent = priceFormatted ? `+${priceFormatted}` : badgeEl.textContent;
      }
    }

    const subtitleEl = widget.querySelector('.gift-box-product-option__subtitle');
    if (subtitleEl) {
      const threshFormatted = widget.dataset.thresholdFormatted;
      subtitleEl.textContent = isFree
        ? 'Your cart qualifies for a complimentary gift box!'
        : `Free on orders over ${threshFormatted || formatMoneyINR(threshold)}`;
    }
  }

  /**
   * PDP Form Input Transformer
   * Transforms form inputs to Shopify multi-item format (items[0] and items[1])
   * when checkbox is checked. Binds to EVERY matching form on the page, since
   * quick-add / quick-view widgets can render more than one add-to-cart form.
   */
  function bindPDPFormInputs() {
    const pdpForms = document.querySelectorAll('.js-product-form, form[action*="/cart/add"]');
    if (!pdpForms.length) return;

    pdpForms.forEach((pdpForm) => bindSingleForm(pdpForm));
  }

  function bindSingleForm(pdpForm) {
    const giftBoxCheckbox = pdpForm.querySelector('.js-gift-box-pdp-checkbox');
    if (!giftBoxCheckbox) return;

    function syncFormInputs() {
      const giftVariantId = giftBoxCheckbox.dataset.giftVariantId;
      if (!giftVariantId) return;

      const isChecked = giftBoxCheckbox.checked;

      // Remove existing dynamic inputs
      pdpForm.querySelectorAll('.js-gift-box-dynamic-input').forEach(el => el.remove());

      const idInput = pdpForm.querySelector('input[name="id"], input[name="items[0][id]"]');
      const qtyInput = pdpForm.querySelector('input[name="quantity"], input[name="items[0][quantity]"]');

      if (isChecked) {
        // 1. Convert main product inputs to items[0]
        if (idInput) idInput.name = 'items[0][id]';
        if (qtyInput) qtyInput.name = 'items[0][quantity]';

        pdpForm.querySelectorAll('[name^="properties["]').forEach(input => {
          if (input.name !== 'properties[_add_gift_box]') {
            const propName = input.name.replace('properties[', '').replace(']', '');
            input.dataset.origName = input.name;
            input.name = `items[0][properties][${propName}]`;
          }
        });

        // 2. Inject items[1] inputs for Gift Box
        const item1Id = document.createElement('input');
        item1Id.type = 'hidden';
        item1Id.name = 'items[1][id]';
        item1Id.value = giftVariantId;
        item1Id.className = 'js-gift-box-dynamic-input';

        const item1Qty = document.createElement('input');
        item1Qty.type = 'hidden';
        item1Qty.name = 'items[1][quantity]';
        item1Qty.value = '1';
        item1Qty.className = 'js-gift-box-dynamic-input';

        pdpForm.appendChild(item1Id);
        pdpForm.appendChild(item1Qty);
      } else {
        // Restore standard names
        if (idInput) idInput.name = 'id';
        if (qtyInput) qtyInput.name = 'quantity';

        pdpForm.querySelectorAll('[name^="items[0][properties]"]').forEach(input => {
          if (input.dataset.origName) {
            input.name = input.dataset.origName;
          }
        });
      }
    }

    if (!giftBoxCheckbox.dataset.syncBound) {
      giftBoxCheckbox.dataset.syncBound = 'true';
      giftBoxCheckbox.addEventListener('change', syncFormInputs);
    }

    // Sync state on form submit as well to guarantee state
    if (!pdpForm.dataset.syncSubmitBound) {
      pdpForm.dataset.syncSubmitBound = 'true';
      pdpForm.addEventListener('submit', () => {
        syncFormInputs();
      }, true);
    }

    // Initial check on page load / bind
    syncFormInputs();
  }

  /**
   * Cart Drawer / Cart Page Checkbox Binding
   */
  function bindCartGiftBoxToggles() {
    const cartToggles = document.querySelectorAll('.js-toggle-cart-gift-box');
    cartToggles.forEach((checkbox) => {
      if (checkbox.dataset.listenerAttached) return;
      checkbox.dataset.listenerAttached = 'true';

      checkbox.addEventListener('change', async (e) => {
        const isChecked = e.target.checked;
        const variantId = e.target.dataset.variantId;
        const lineKey = e.target.dataset.lineKey;

        if (isToggling) {
          // Another toggle is mid-flight — revert this one instead of
          // silently dropping it so the checkbox state always matches
          // what actually happened.
          e.target.checked = !isChecked;
          return;
        }
        isToggling = true;
        checkbox.disabled = true;

        try {
          if (isChecked) {
            // Defensive re-check: don't add a duplicate line item if the
            // gift box is already in the cart (e.g. added from the PDP a
            // moment ago, before this checkbox had a chance to sync).
            const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
            const cart = await cartRes.json();
            const existing = cart.items.find(item => String(item.variant_id) === String(variantId));

            if (existing) {
              checkbox.dataset.lineKey = existing.key;
            } else {
              await postCartJson('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
            }
          } else {
            const targetId = lineKey || String(variantId);
            await postCartJson('cart/change.js', { id: targetId, quantity: 0 });
          }
          refreshCartUI(false);
        } catch (err) {
          console.error('Error updating Gift Box in cart:', err);
          checkbox.checked = !isChecked;
        } finally {
          checkbox.disabled = false;
          isToggling = false;
        }
      });
    });
  }

  /**
   * POST helper for cart/add.js and cart/change.js that treats non-2xx
   * responses as errors (e.g. sold-out variant, invalid line key) instead of
   * silently resolving.
   */
  async function postCartJson(path, body) {
    const res = await fetch(window.Shopify.routes.root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      let message = `Request to ${path} failed with status ${res.status}`;
      try {
        const errJson = await res.json();
        if (errJson && errJson.description) message = errJson.description;
        else if (errJson && errJson.message) message = errJson.message;
      } catch (_) {
        // response wasn't JSON — keep the generic message
      }
      throw new Error(message);
    }

    return res.json();
  }

  /**
   * Check Cart Subtotal and auto-manage the Gift Box line item.
   *
   * SAME-VARIANT SETUP (confirmed): the store uses ONE gift box variant
   * whose price is made ₹0 via a Shopify (automatic) discount when the
   * cart total crosses ₹20k, rather than swapping between two variants.
   * Because it's the same variant either way, this script has NO reliable
   * way to tell "free state" apart from "paid state" from the variant id
   * alone — so it must NOT auto-remove the item just because the cart is
   * under ₹20k. That was the bug: on every 'on:cart:after-merge' (which
   * also fires right after the gift box itself is added), the old logic
   * saw "gift item present + cart < ₹20k" and deleted it immediately,
   * even when the customer had just paid-added it on purpose.
   *
   * So by default this function does nothing for a single-variant setup —
   * pricing is left entirely to Shopify's discount engine, and the gift
   * box line item is only ever added/removed by explicit user action
   * (checkbox toggle / form submit).
   *
   * OPTIONAL two-variant mode: if the widget provides BOTH
   * data-gift-variant-id (paid) and data-gift-free-variant-id (a distinct
   * free variant), the script auto-swaps between them as the cart crosses
   * the threshold. Only enable this if paid and free are genuinely
   * different variant IDs in your setup.
   */
  async function checkCartThresholdAndUpdate() {
    if (isThresholdChecking) return;
    const widget = document.querySelector('.gift-box-cart-widget');
    if (!widget) return;

    const paidVariantId = widget.dataset.giftVariantId;
    const freeVariantId = widget.dataset.giftFreeVariantId; // optional, must differ from paidVariantId

    // Not a genuine two-variant setup — nothing to auto-manage.
    if (!freeVariantId || freeVariantId === paidVariantId) return;

    try {
      const res = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await res.json();

      const paidItem = cart.items.find(item => String(item.variant_id) === String(paidVariantId));
      const freeItem = cart.items.find(item => String(item.variant_id) === String(freeVariantId));
      const overThreshold = cart.total_price >= THRESHOLD;

      isThresholdChecking = true;

      if (overThreshold && paidItem && !freeItem) {
        await postCartJson('cart/change.js', { id: paidItem.key, quantity: 0 });
        await postCartJson('cart/add.js', { items: [{ id: freeVariantId, quantity: 1 }] });
        refreshCartUI(false);
      } else if (!overThreshold && freeItem) {
        await postCartJson('cart/change.js', { id: freeItem.key, quantity: 0 });
        refreshCartUI(false);
      }
    } catch (err) {
      console.error('Error checking cart threshold:', err);
    } finally {
      isThresholdChecking = false;
    }
  }

  /**
   * Refreshes Theme Cart UI and re-binds gift box handlers once the drawer
   * markup updates. Uses requestAnimationFrame first (catches synchronous
   * re-renders) plus a fallback timeout for themes that re-render async.
   * Both calls are safe since bindPDPFormInputs/bindCartGiftBoxToggles are
   * idempotent (guarded by dataset flags).
   */
  function refreshCartUI(openDrawer = false) {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

    if (openDrawer) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
    }

    requestAnimationFrame(() => {
      initGiftBox();
    });

    setTimeout(() => {
      initGiftBox();
    }, 500);
  }

})();